'use client';

import { memo } from 'react';
import { Box } from '@terra/ui-styled-system';
import {
  useReactFlow,
  type Node,
  BaseEdge,
  getBezierPath,
  type EdgeProps,
} from '@xyflow/react';
import { GRAPH_NODE_COLOR, type AssetNodeData } from './constants';
import { getEdgeCoordinates } from './external-network-graph-edge-geometry';

const MAX_LABEL_WIDTH = 120;
const LABEL_HEIGHT = 80;

const ExternalNetworkGraphEdgeComponent = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  source,
  target,
  style,
  markerEnd,
  label,
  data,
}: EdgeProps) => {
  const { getNode } = useReactFlow<Node<AssetNodeData>>();
  const sourceNode = getNode(source);
  const targetNode = getNode(target);
  const targetColor =
    GRAPH_NODE_COLOR[targetNode?.data?.entityKind ?? 'DOMAIN'] ?? '#6b7280';
  const edgeCoordinates = getEdgeCoordinates({
    sourceNode,
    targetNode,
    sourceX,
    sourceY,
    targetX,
    targetY,
  });

  const [edgePath] = getBezierPath({
    sourceX: edgeCoordinates.sourceX,
    sourceY: edgeCoordinates.sourceY,
    sourcePosition: edgeCoordinates.sourcePosition,
    targetX: edgeCoordinates.targetX,
    targetY: edgeCoordinates.targetY,
    targetPosition: edgeCoordinates.targetPosition,
  });

  const edgeData = (data ?? {}) as {
    simplified?: boolean;
    showEdgeLabels?: boolean;
  };
  const labelText = label ? String(label) : null;
  const labelX = (edgeCoordinates.sourceX + edgeCoordinates.targetX) / 2;
  const labelY = (edgeCoordinates.sourceY + edgeCoordinates.targetY) / 2;

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          ...style,
          strokeWidth: 2,
          stroke: targetColor,
        }}
        markerEnd={markerEnd}
      />
      {labelText &&
        edgeData.showEdgeLabels !== false &&
        !edgeData.simplified && (
          <g>
            <foreignObject
              x={labelX - MAX_LABEL_WIDTH / 2}
              y={labelY - LABEL_HEIGHT / 2}
              width={MAX_LABEL_WIDTH}
              height={LABEL_HEIGHT}
              style={{
                opacity: style?.opacity ?? 1,
                pointerEvents: 'none',
              }}
            >
              <Box
                h="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box
                  padding="0 8px"
                  borderRadius="sm"
                  backgroundColor="bg.neutral.hoverWhite"
                  fontSize="11px"
                  color="fg.muted"
                  fontWeight={400}
                  textAlign="center"
                  wordWrap="break-word"
                  overflowWrap="break-word"
                  maxWidth={`${MAX_LABEL_WIDTH}px`}
                  lineHeight="1.3"
                  whiteSpace="normal"
                  boxSizing="border-box"
                  style={{
                    display: '-webkit-box',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    WebkitLineClamp: '5',
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {labelText}
                </Box>
              </Box>
            </foreignObject>
          </g>
        )}
    </>
  );
};

export const ExternalNetworkGraphEdge = memo(ExternalNetworkGraphEdgeComponent);
ExternalNetworkGraphEdge.displayName = 'ExternalNetworkGraphEdge';

export const externalNetworkEdgeTypes = {
  externalNetworkEdge: ExternalNetworkGraphEdge,
};
